'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import {
  LayoutDashboard,
  FileText,
  PackageSearch,
  ClipboardList,
  Heart,
  Copy,
  HelpCircle,
  Settings,
} from 'lucide-react';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { useCart, CartSidebar } from '@paeffgen/react/crm-documents/feature-material-requests';
import {
  ThemeToggle,
  useNavHighlight,
  type NavHighlightKey,
} from '@paeffgen/react/crm-documents/ui';
import { clsx } from 'clsx';
import { MobileScrollIndicator } from './mobile-scroll-indicator';
import { MobileBottomNav, type NavItem } from './mobile-bottom-nav';
import { MobileHeader } from './mobile-header';
import { PillSheet } from './pill-sheet';
import { CustomerContextPopover } from './customer-context-popover';
import { ReportProblemButton } from './report-problem-button';
import { ImpersonationBanner } from './impersonation-banner';
import { HIGHLIGHT_KEY_FOR_ROUTE } from './nav-config';
import { OnboardingDialog } from '../notifications/OnboardingDialog';

/** Routes that only work with a single debitor (disabled when multiple are selected) */
const SINGLE_CUSTOMER_ROUTES = ['/favoriten', '/projekte', '/materialanfrage'];

const NAV_ITEMS: NavItem[] = [
  { href: '/', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/belege', label: 'Belege', icon: FileText },
  { href: '/artikelsuche', label: 'Artikelsuche', icon: PackageSearch },
  { href: '/favoriten', label: 'Favoriten', icon: Heart },
  { href: '/projekte', label: 'Projekte', icon: Copy },
  { href: '/materialanfrage', label: 'Anfrage', icon: ClipboardList },
  // Einstellungen — bewusst NICHT in NAV_ITEMS (sonst auch in der Mobile-Bottom-Nav,
  // wo der Platz knapp ist). Desktop: eigener Sidebar-Eintrag unten; mobil über das
  // PillSheet (Usermenü) erreichbar — analog zu „Hilfe".
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { isAdmin, allCustomers, selectedCustomers } = useAdmin();
  // Banner is visible whenever admin has "Alle Kunden" on — independent of debitor count.
  // Layout must reserve space for the banner in all of those modes.
  const isAdminBannerVisible = isAdmin && allCustomers;
  const { isOpen: cartOpen, toggleCart, itemCount } = useCart();
  const { highlights, clearHighlight } = useNavHighlight();
  const [pillSheetOpen, setPillSheetOpen] = useState(false);

  // Clear highlight when user visits the matching route — handles both:
  // 1. navigation to the route (pathname change)
  // 2. mark() called while already on the route (highlights change)
  useEffect(() => {
    for (const [route, key] of Object.entries(HIGHLIGHT_KEY_FOR_ROUTE) as [
      string,
      NavHighlightKey,
    ][]) {
      if (
        highlights.has(key) &&
        (pathname === route || pathname.startsWith(route + '/'))
      ) {
        clearHighlight(key);
      }
    }
  }, [pathname, highlights, clearHighlight]);

  // Single-customer features disabled: "Alle Kunden" is on but no single debitor is targeted
  const noSingleCustomer = allCustomers && selectedCustomers.length !== 1;

  // Redirect to dashboard when current route becomes disabled (multi-debitor)
  useEffect(() => {
    if (noSingleCustomer && SINGLE_CUSTOMER_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'))) {
      router.replace('/');
    }
  }, [noSingleCustomer, pathname, router]);

  return (
    <div className={clsx("min-h-[100dvh] bg-surface flex flex-col md:flex-row", isAdminBannerVisible && "pt-10")}>
      {/* Cross-Login Impersonation Banner */}
      <ImpersonationBanner />

      {/* Mobile Header */}
      <MobileHeader
        onPillClick={() => setPillSheetOpen(true)}
        bannerVisible={isAdminBannerVisible}
      />

      {/* Mobile Scroll Indicator */}
      <div className={clsx("md:hidden sticky z-30 bg-surface/80 backdrop-blur-sm", isAdminBannerVisible ? "top-[6rem]" : "top-14")}>
        <MobileScrollIndicator scrollContainerId="main-scroll-container" />
      </div>

      {/* Desktop Sidebar */}
      <aside className={clsx(
        // md:z-30 hebt die gesamte Sidebar-Ebene über den Main-Content, damit
        // das überstehende Kontext-Popover sichtbar bleibt (unter FAB/Modal/Cart).
        "hidden md:flex md:sticky md:z-30 flex-col w-64 bg-white border-r border-zinc-200 shrink-0",
        isAdminBannerVisible ? "md:top-10 md:h-[calc(100dvh-2.5rem)]" : "md:top-0 md:h-[100dvh]"
      )}>
        {/* Logo */}
        <div className="p-6 border-b border-zinc-100">
          <Link href="/">
            <Image
              src="/logo-dachbaustoffe_color.svg"
              alt="Paeffgen Dachbaustoffe"
              width={200}
              height={40}
              className="w-full h-auto mb-3 object-contain object-left dark:hidden"
              priority
            />
            <Image
              src="/logo-dachbaustoffe_dark.svg"
              alt="Paeffgen Dachbaustoffe"
              width={200}
              height={40}
              className="hidden w-full h-auto mb-3 object-contain object-left dark:block"
              priority
            />
          </Link>
          <p className="text-xs text-text-muted mt-1">
            Päffgen Pro B2B Portal
          </p>
        </div>

        {/* Kunden-Kontext: kompakte Zeile + Popover (Konto, Cross-Login,
            Debitor-Suche, Ansprechpartner) — hält die Spalte schlank. */}
        <CustomerContextPopover />

        {/* Navigation */}
        <nav className="flex-1 px-4 pb-4 pt-8 space-y-1 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive =
              pathname === item.href ||
              (item.href !== '/' && pathname.startsWith(item.href));
            const isMaterialanfrage = item.href === '/materialanfrage';

            // Disable single-customer routes when admin has multiple customers selected
            const isDisabled = noSingleCustomer && SINGLE_CUSTOMER_ROUTES.includes(item.href);

            const highlightKey = HIGHLIGHT_KEY_FOR_ROUTE[item.href];
            const isHighlighted =
              highlightKey !== undefined &&
              highlights.has(highlightKey) &&
              !isActive &&
              !isDisabled;

            if (isDisabled) {
              return (
                <span
                  key={item.href}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold text-zinc-300 cursor-not-allowed"
                  title="Nur für einzelne Debitoren verfügbar"
                >
                  <Icon size={20} className="text-zinc-300" />
                  {item.label}
                </span>
              );
            }

            return (
              <Link
                key={item.href}
                href={item.href}
                className={clsx(
                  'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-colors',
                  isActive
                    ? 'bg-orange-50 text-primary'
                    : isHighlighted
                      ? 'bg-green-50 text-secondary hover:bg-green-100'
                      : 'text-text-muted hover:bg-zinc-100 hover:text-secondary',
                )}
              >
                <Icon
                  size={20}
                  className={
                    isActive
                      ? 'text-primary'
                      : isHighlighted
                        ? 'text-green-600'
                        : 'text-text-muted'
                  }
                />
                {item.label}
                {isHighlighted && <span className="sr-only"> (neu)</span>}
                {isMaterialanfrage && itemCount > 0 && (
                  <span className="ml-auto bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {itemCount}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Einstellungen — bewusst NICHT in NAV_ITEMS (sonst auch in der
              Mobile-Bottom-Nav). Eigener Sidebar-Eintrag; mobil über das PillSheet. */}
          {(() => {
            const settingsActive =
              pathname === '/einstellungen' ||
              pathname.startsWith('/einstellungen/');
            return (
              <Link
                href="/einstellungen"
                className={clsx(
                  'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-colors',
                  settingsActive
                    ? 'bg-orange-50 text-primary'
                    : 'text-text-muted hover:bg-zinc-100 hover:text-secondary',
                )}
              >
                <Settings
                  size={20}
                  className={settingsActive ? 'text-primary' : 'text-text-muted'}
                />
                Einstellungen
              </Link>
            );
          })()}

          {/* Hilfe — bewusst NICHT in NAV_ITEMS (sonst auch in der Mobile-Bottom-Nav).
              Eigener Sidebar-Eintrag; mobil ist Hilfe über das PillSheet erreichbar. */}
          {(() => {
            const helpActive =
              pathname === '/informationen' ||
              pathname.startsWith('/informationen/');
            return (
              <Link
                href="/informationen"
                className={clsx(
                  'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-colors',
                  helpActive
                    ? 'bg-orange-50 text-primary'
                    : 'text-text-muted hover:bg-zinc-100 hover:text-secondary',
                )}
              >
                <HelpCircle
                  size={20}
                  className={helpActive ? 'text-primary' : 'text-text-muted'}
                />
                Hilfe
              </Link>
            );
          })()}
        </nav>

        {/* Fehler melden — Sidebar-Footer-Aktion über dem Theme-Umschalter */}
        <div className="mt-auto border-t border-zinc-100 px-4 py-2">
          <ReportProblemButton variant="slim" />
        </div>

        {/* Theme-Umschalter im Sidebar-Footer */}
        <div className="border-t border-zinc-100 p-4">
          <ThemeToggle className="w-full justify-center" />
        </div>
      </aside>

      {/* Main Content */}
      <main
        id="main-scroll-container"
        className={clsx(
          "flex-1 min-w-0 md:overflow-y-auto pb-20 md:pb-0",
          isAdminBannerVisible
            ? "h-[calc(100dvh-6rem)] md:h-[calc(100dvh-2.5rem)]"
            : "h-[calc(100dvh-3.5rem)] md:h-[100dvh]"
        )}
      >
        <div className="container mx-auto max-w-7xl p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* Cart Sidebar */}
      <CartSidebar />

      {/* Mobile Bottom Navigation — Dashboard ist via Logo im Header erreichbar */}
      <MobileBottomNav
        items={NAV_ITEMS.filter((item) => item.href !== '/').map((item) => ({
          ...item,
          disabled: noSingleCustomer && SINGLE_CUSTOMER_ROUTES.includes(item.href),
        }))}
        materialanfrageCount={itemCount}
      />

      {/* Mobile Pill Sheet (cross-login + account) */}
      <PillSheet open={pillSheetOpen} onClose={() => setPillSheetOpen(false)} />

      {/* Benachrichtigungs-Onboarding — nur für authentifizierte Portal-Nutzer,
          da notificationPrefs einen Token benötigt. AppShell wird ausschließlich
          für authentifizierte Routen gerendert (siehe client-layout.tsx). */}
      <OnboardingDialog />
    </div>
  );
}
